import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * Wraps any route that requires authentication.
 * While session restore is in progress, shows a loading screen.
 * Once resolved: unauthenticated users are redirected to /auth/login.
 */
export default function ProtectedRoute({ children, adminOnly = false, affiliateManagerOnly = false, salesMarketingOnly = false, serverSecretsManagerOnly = false }) {
  const { isAuthenticated, isAdmin, user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-reset-cream">
        <p className="text-reset-brown text-sm">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (affiliateManagerOnly && !user?.can_manage_affiliates) {
    return <Navigate to="/admin" replace />;
  }

  if (salesMarketingOnly && !(user?.can_manage_affiliates || user?.can_see_sales_marketing)) {
    return <Navigate to="/admin" replace />;
  }

  if (serverSecretsManagerOnly && !user?.can_manage_server_secrets) {
    return <Navigate to="/admin" replace />;
  }

  // Paused members (Jenn, 2026-10-07): the Hub is only Settings until the
  // pause ends — on its own, or when they end it there. Team365 staff are
  // never locked out of their tools.
  const paused = !!user?.pause_ends_at && new Date(user.pause_ends_at) > new Date();
  if (paused && !isAdmin && !['/dashboard/settings', '/auth/set-password'].includes(location.pathname)) {
    return <Navigate to="/dashboard/settings" replace />;
  }

  return children;
}
